import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import '@veilstream/ui/tokens.css'
import './index.css'
import App from './App'
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || 'https://1ce0e2832a8330ca4e480f160792518d@o4507816790458368.ingest.us.sentry.io/4508215229874176',
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
  environment: import.meta.env.VITE_ENV || 'unknown',
})

// After a frontend deploy, an in-session user may lazy-load a hashed chunk
// that is no longer in S3 (or the browser cached a stale index.html). Vite
// dispatches `vite:preloadError` when a dynamic import fails. Reload once
// to fetch the new index.html and its current chunk filenames. Guard with a
// sessionStorage flag so we never reload-loop.
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_FLAG = 'vs:reloaded-after-preload-error'
  if (sessionStorage.getItem(RELOAD_FLAG) === '1') {
    Sentry.captureException(
      (event as Event & { payload?: unknown }).payload ?? new Error('vite:preloadError (persisted after reload)'),
      { tags: { kind: 'chunk-load' } },
    )
    return
  }
  sessionStorage.setItem(RELOAD_FLAG, '1')
  window.location.reload()
})

// Clear the reload flag once a new session successfully reaches here on a
// subsequent page load — i.e. the reload worked.
setTimeout(() => {
  sessionStorage.removeItem('vs:reloaded-after-preload-error')
}, 5000)

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')

createRoot(rootElement).render(
  <StrictMode>
    <NotificationProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </NotificationProvider>
  </StrictMode>,
)
